import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";
import { mapConversationsToRows } from "../../utils/conversationHistoryMappers";

const emptyPagination = {
  page: 1,
  limit: 5,
  total: 0,
  total_pages: 0,
  from: 0,
  to: 0,
  preview: true,
};

/**
 * GET /ticket/get-ticket/:ticketId/user-history
 * Sidebar History preview (pass ticket id).
 */
export const getTicketUserHistory = createAsyncThunk(
  "ticket/getTicketUserHistory",
  async (
    { ticketId, page = 1, limit = 5, search, type, status } = {},
    { getState, rejectWithValue }
  ) => {
    if (!ticketId) return rejectWithValue("Missing ticketId");
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (search) params.set("search", search);
      if (type && type !== "all") params.set("type", type);
      if (status && status !== "all") params.set("status", status);

      const response = await axios.get(
        `${ROOT_URL}/ticket/get-ticket/${ticketId}/user-history?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return { ...response.data, _ticketId: String(ticketId) };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  ticketId: null,
  customer: null,
  summary: { total: 0, label: "" },
  conversations: [],
  rows: [],
  pagination: emptyPagination,
};

const getTicketUserHistorySlice = createSlice({
  name: "getTicketUserHistory",
  initialState,
  reducers: {
    resetTicketUserHistory: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTicketUserHistory.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        const nextId = action.meta.arg?.ticketId
          ? String(action.meta.arg.ticketId)
          : null;
        if (nextId && state.ticketId && nextId !== state.ticketId) {
          state.conversations = [];
          state.rows = [];
          state.summary = { total: 0, label: "" };
          state.customer = null;
          state.pagination = emptyPagination;
        }
      })
      .addCase(getTicketUserHistory.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload?.data || {};
        state.ticketId = action.payload?._ticketId || state.ticketId;
        state.customer = payload.customer || null;
        state.summary = payload.summary || { total: 0, label: "" };
        state.conversations = Array.isArray(payload.conversations)
          ? payload.conversations
          : [];
        state.rows = mapConversationsToRows(state.conversations);
        state.pagination = payload.pagination || emptyPagination;
      })
      .addCase(getTicketUserHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetTicketUserHistory } = getTicketUserHistorySlice.actions;
export default getTicketUserHistorySlice.reducer;
